/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable sort-keys */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { apiCall, guestIdParamLocalStorage } from '../../utils/apiCall';
import { getApiUrl } from '../../utils/apiHandler';
import { distributionChannel } from '../../utils/consts';
import { digitalClientMapper } from '../../utils/targetMappings';
import {
  csQuotationIsHistoryPayload,
  csQuotationIsHistoryResponse,
  ListByVehiclePayload,
  ListByVehicleResponse,
  MasterAgreement,
  MontlyRateCalculationPayload,
  MontlyRateCalculationPayloadReduced,
  MontlyRateCalculationResponse,
  mrcResponse,
  RegularPayment,
  RetrieveQuotationIdDataResponse
} from '../_declarations/target';
import { setPromoCodeValidation, setSelectedItem } from '../slices/appSlice';

export const listByVehicleApi = createAsyncThunk<
  ListByVehicleResponse,
  ListByVehiclePayload,
  { rejectValue: { code: string; userMessage: string } }
>(
  'financialCalculator/list-by-vehicle',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state: any = getState();

      const {
        app: { consumerPayload /* testPrupose */ },
        auth: { value }
      } = state;

      /* if (testPrupose) {
        return mockResponseListByVehicle;
      } */

      const listByVehicle = getApiUrl('listByVehicle');

      const config = {
        data: payload,
        headers: {
          Authorization: value || '',
          SessionId: window.localStorage.getItem(guestIdParamLocalStorage) || ''
        },
        method: 'POST',
        url: listByVehicle
      };

      const response: ListByVehicleResponse = await apiCall(config);

      const masterAgreement: MasterAgreement[] = [];

      const defaultMA = response.masterAgreement.find(
        (ma) =>
          ma.zzDigCliDef ===
          digitalClientMapper[consumerPayload?.context.siteCode]
      );

      if (defaultMA) {
        masterAgreement.push(defaultMA);
      }

      masterAgreement.push(
        ...response.masterAgreement.filter(
          (ma) =>
            ma.zzDigCliDef !==
            digitalClientMapper[consumerPayload?.context.siteCode]
        )
      );

      const reorderedResponse = {
        ...response,
        masterAgreement
      };

      dispatch(
        setSelectedItem(
          reorderedResponse?.masterAgreement?.[0]?.masterAgreement || ''
        )
      );

      return reorderedResponse;
    } catch (error: any) {
      return rejectWithValue({
        code: error.code || 'UNKNOWN_ERROR',
        userMessage: error.message || 'Something went wrong'
      });
    }
  }
);

type AbortControllerPlus = AbortController & { payload?: number | string };
let controller: AbortControllerPlus | null = null;

export const montlyRateCalculationApi = createAsyncThunk<
  mrcResponse,
  MontlyRateCalculationPayloadReduced,
  { rejectValue: { code: string; userMessage: string } }
>(
  'financialCalculator/montly-rate-calculation',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state: any = getState();

      if (controller && controller.payload === payload?.masterAgreement) {
        controller.abort();
      }

      if (payload?.widgetSelection?.context?.discountCode) {
        dispatch(setPromoCodeValidation(true));
      }

      controller = new AbortController();
      controller.payload = payload?.masterAgreement;

      const montlyRateCalculation = getApiUrl('montlyRateCalculation');

      const {
        app: { consumerPayload },
        financialCalculator: { listByVehicle, montlyRateCalculationPayloads }
      } = state;

      const confirmPayload: MontlyRateCalculationPayload =
        montlyRateCalculationPayloads?.[payload?.masterAgreement];

      // BEGIN -- create paramenters fallback from listbyvehicle
      const maFromListByVehicle: MasterAgreement =
        listByVehicle?.masterAgreement?.find(
          (ma: MasterAgreement) =>
            ma.masterAgreement === payload?.masterAgreement
        );

      const month =
        maFromListByVehicle.options.month.find((m) => m.isDefault)?.month || 0;
      const km =
        maFromListByVehicle.options.km.find((okm) => okm.isDefault)?.km || 0;
      const globalMileage = (km / 12) * month;

      const fallBackParams = {
        cashDeposit: maFromListByVehicle.downPaymentAmount,
        globalMileage,
        km,
        masterAgreement: maFromListByVehicle.masterAgreement,
        mileAllowKm: Number(maFromListByVehicle.mileAllowKm),
        mileAllowKmPerc: Number(maFromListByVehicle.mileAllowKmPerc),
        month,
        services: maFromListByVehicle.services.filter(
          (s) =>
            s.isMandatory ||
            s.isMandatoryCategory ||
            s.zzRecomCategory ||
            !s.isVisible
        )
      };
      // END -- create paramenters fallback from listbyvehicle
      const apiPayload: MontlyRateCalculationPayload = {
        distributionChannel,
        materialInfo: listByVehicle.materialInfo,
        pricingProcedure: payload.pricingProcedure,
        widgetSelection: {
          context: consumerPayload?.context,
          parameters: {
            ...(confirmPayload?.widgetSelection?.parameters || fallBackParams),
            ...payload.widgetSelection.parameters
          },
          vehicle: {
            accessory: payload.widgetSelection.vehicle.accessory,
            fiscalProperties: consumerPayload?.vehicle.fiscalProperties,
            manufacturerCode:
              consumerPayload?.vehicle.manufacturerCode ||
              consumerPayload?.vehicle.materialId,
            materialId: consumerPayload?.vehicle.materialId,
            options: consumerPayload?.vehicle.options,
            otrCostsIncluded: consumerPayload?.vehicle.otrCostsIncluded,
            pricing: consumerPayload?.vehicle.pricing,
            internalColCode: consumerPayload?.vehicle.internalColCode,
            externalColCode: consumerPayload?.vehicle.externalColCode,
            internalColDesc: consumerPayload?.vehicle.internalColDesc,
            externalColDesc: consumerPayload?.vehicle.externalColDesc
          }
        }
      };

      // Montly rate calculation does not need to be called with color codes in the payload and added escape to eslint

      const {
        externalColCode,
        externalColDesc,
        internalColCode,
        internalColDesc,
        ...vehicle
      } = apiPayload.widgetSelection.vehicle;

      if (payload.widgetSelection.context?.discountCode) {
        apiPayload.widgetSelection.context = {
          ...apiPayload.widgetSelection.context,
          discountCode: payload.widgetSelection.context.discountCode
        };
      }

      const config = {
        data: {
          ...apiPayload,
          widgetSelection: { ...apiPayload.widgetSelection, vehicle }
        },
        headers: {
          Authorization: state?.auth?.value || '',
          SessionId:
            window.localStorage.getItem(guestIdParamLocalStorage) || '',
          'x-trace-details': 'true'
        },
        method: 'POST',
        signal: controller.signal,
        url: montlyRateCalculation
      };

      const response: MontlyRateCalculationResponse = await apiCall(config);

      return {
        apiPayload,
        response,
        masterAgreement: payload.widgetSelection.parameters.masterAgreement
      } as mrcResponse;
    } catch (error: any) {
      return rejectWithValue({
        code: error.code || 'UNKNOWN_ERROR',
        userMessage: error.message || 'Something went wrong'
      });
    } finally {
      dispatch(setPromoCodeValidation(false));
    }
  }
);

export const createSaveQuotationIdHistory = createAsyncThunk<
  csQuotationIsHistoryResponse,
  { masterAgreement: string; regularPayment: RegularPayment },
  { rejectValue: { code: string; userMessage: string } }
>(
  'financialCalculator/montly-rate-calculation',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();

      const montlyRateCalculation = getApiUrl('createSaveQuotationIdHistory');

      const { masterAgreement, regularPayment } = payload;

      const widgetPayloads =
        state?.financialCalculator?.montlyRateCalculationPayloads;

      const confirmPayload: MontlyRateCalculationPayload =
        widgetPayloads?.[masterAgreement];

      if (confirmPayload) {
        const apiPayload: csQuotationIsHistoryPayload = {
          distributionChannel: confirmPayload.distributionChannel,
          pricingProcedure: confirmPayload.pricingProcedure,
          regularPayment,
          widgetSelection: confirmPayload.widgetSelection,
          materialInfo: confirmPayload.materialInfo
        };

        const config = {
          data: apiPayload,
          headers: {
            Authorization: state?.auth?.value || '',
            SessionId:
              window.localStorage.getItem(guestIdParamLocalStorage) || '',
            'x-trace-details': 'true'
          },
          method: 'POST',
          url: montlyRateCalculation
        };

        const response: csQuotationIsHistoryResponse = await apiCall(config);

        return response;
      }
      return rejectWithValue({
        code: 'PAYLOAD_ERROR',
        userMessage: 'Confirm payload not found'
      });
    } catch (error: any) {
      return rejectWithValue({
        code: error.code || 'UNKNOWN_ERROR',
        userMessage: error.message || 'Something went wrong'
      });
    }
  }
);

export const retrieveQuotationIdData = createAsyncThunk<
  RetrieveQuotationIdDataResponse,
  { quotationId: string },
  { rejectValue: { code: string; userMessage: string } }
>(
  'financialCalculator/retrieve-quotation-id-data',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state: any = getState();

      const {
        auth: { value }
      } = state;

      const retrieveQuotationIdDataAPI = getApiUrl('retrieveQuotationIdData');

      const config = {
        headers: {
          Authorization: value || '',
          SessionId:
            window.localStorage.getItem(guestIdParamLocalStorage) || '',
          ...payload
        },
        method: 'GET',
        url: retrieveQuotationIdDataAPI
      };

      const response: RetrieveQuotationIdDataResponse = await apiCall(config);

      dispatch(
        setSelectedItem(response.widgetSelection.parameters.masterAgreement)
      );

      return response;
    } catch (error: any) {
      return rejectWithValue({
        code: error.code || 'UNKNOWN_ERROR',
        userMessage: error.message || 'Something went wrong'
      });
    }
  }
);
